import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, Package, ShoppingBag, Truck, User, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fmtPrice, STATUS_LABELS, STATUS_STYLES } from "../lib/shop";
import { FadeUp } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [favorites, setFavorites] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
      api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => setFavorites([]));
    }
  }, [user]);

  if (user === undefined) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-24">
        <div className="h-64 animate-pulse rounded-lg bg-skytint" />
      </main>
    );
  }
  if (!user) return <Navigate to="/autentificare" replace />;

  const activeOrders = (orders || []).filter((o) => !["livrata", "anulata"].includes(o.status)).length;

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="account-page">
      <FadeUp>
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-navy p-8 sm:p-10">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-sky2/20">
              <User className="h-6 w-6 text-sky-light" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-light">Contul meu</p>
              <h1 className="font-display text-2xl font-extrabold text-white sm:text-3xl" data-testid="account-name">
                Salut, {user.name}!
              </h1>
              <p className="text-sm text-sky-light">{user.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-rose-400 hover:text-rose-300"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Deconectare
          </button>
        </div>
      </FadeUp>

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {[
          { icon: ShoppingBag, label: "Total comenzi", value: orders?.length ?? "—" },
          { icon: Truck, label: "Comenzi în curs", value: orders ? activeOrders : "—" },
          { icon: Heart, label: "Produse favorite", value: favorites?.length ?? "—" },
        ].map((s) => (
          <FadeUp key={s.label}>
            <div className="flex items-center gap-4 rounded-lg border border-line bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-skytint">
                <s.icon className="h-5 w-5 text-navy" />
              </span>
              <div>
                <p className="font-display text-xl font-extrabold text-navy">{s.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-light">{s.label}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.05} className="mt-10">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy sm:text-2xl">
          <Heart className="h-5 w-5 text-rose-500" /> Produse favorite
        </h2>
      </FadeUp>
      {favorites === null ? (
        <div className="mt-6 h-40 animate-pulse rounded-lg bg-skytint" />
      ) : favorites.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border border-dashed border-line py-14 text-center" data-testid="favorites-empty">
          <Heart className="h-8 w-8 text-line" />
          <p className="max-w-sm text-sm font-semibold text-muted2">
            Nu ai produse favorite încă. Apasă pe inimioara de pe orice produs pentru a-l salva aici.
          </p>
          <Link to="/produse" className="rounded-lg bg-navy px-6 py-3 text-sm font-bold text-white hover:bg-navy" data-testid="favorites-empty-shop-link">
            Descoperă produsele
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="favorites-grid">
          {favorites.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}

      <FadeUp delay={0.1} className="mt-12">
        <h2 className="font-display text-xl font-bold text-navy sm:text-2xl">Istoric comenzi</h2>
      </FadeUp>

      {orders === null ? (
        <div className="mt-6 h-40 animate-pulse rounded-lg bg-skytint" />
      ) : orders.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border border-dashed border-line py-16 text-center" data-testid="orders-empty">
          <Package className="h-8 w-8 text-line" />
          <p className="text-sm font-semibold text-muted2">Nu ai nicio comandă încă.</p>
          <Link to="/produse" className="rounded-lg bg-navy px-6 py-3 text-sm font-bold text-white hover:bg-navy" data-testid="orders-empty-shop-link">
            Descoperă produsele
          </Link>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="rounded-lg border border-line bg-white p-6" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-base font-bold text-navy">Comanda {o.number}</p>
                  <p className="text-xs text-sky-light">
                    {new Date(o.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
                <span className={`rounded-full px-4 py-1.5 text-xs font-bold ${STATUS_STYLES[o.status] || "bg-skytint text-muted2"}`} data-testid={`order-status-${o.id}`}>
                  {STATUS_LABELS[o.status] || o.status}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {o.items.map((i) => (
                  <span key={i.product_id} className="rounded-lg bg-skytint px-3 py-1.5 text-xs font-semibold text-muted2">
                    {i.qty}× {i.name}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-skytint pt-4 text-sm">
                <span className="text-muted2">Plată ramburs la livrare</span>
                <span className="font-display text-lg font-extrabold text-navy">{fmtPrice(o.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
